import { useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Badge,
  Button,
  ChannelFormatPreview,
  Card,
  CheckCircleIcon,
  Cluster,
  ConnectorIcon,
  ConnectorStatusBadge,
  EmptyState,
  EyeIcon,
  Field,
  GlobeIcon,
  Grid,
  Input,
  Inspector,
  LayersIcon,
  LinkIcon,
  LockIcon,
  Modal,
  Page,
  QueryState,
  RocketIcon,
  Select,
  ShieldIcon,
  Stack,
  StatStrip,
  Table,
  TargetIcon,
  TextArea,
  cx,
  type Column,
  type InspectorField,
} from '@integra/ui';
import { BrandLogo } from './brandLogos.js';
import type {
  ConnectorConfig,
  ConnectorHealth,
  ConnectorReviewLevel,
  ConnectorSetupGuide,
  Destination,
  PublishTarget,
  Source,
  SyncSummary,
} from '../api/types.js';
import { connectorForm, type ConnectorField } from '../connectors/catalog.js';

/** Provenance + live detail for a channel — the same drawer every object opens. */
function channelInspectorFields(
  config: ConnectorConfig,
  entry: ConnectorHealth | undefined,
): InspectorField[] {
  const fields: InspectorField[] = [
    { label: 'Connector', value: <span className="font-mono text-xs">{config.connectorKey}</span> },
    { label: 'Credentials', value: config.hasCredentials ? 'Stored (write-only)' : 'Missing' },
    {
      label: 'Health',
      value:
        entry === undefined ? 'Not yet checked' : entry.healthy ? 'Healthy' : 'Needs attention',
    },
  ];
  if (entry !== undefined) {
    fields.push({ label: 'Circuit', value: entry.circuitState });
    if (entry.detail !== null) fields.push({ label: 'Detail', value: entry.detail });
    if (entry.quota !== null) {
      fields.push({
        label: 'Quota',
        value: `${entry.quota.remaining.toLocaleString()} / ${entry.quota.limit.toLocaleString()} left`,
      });
    }
  }
  if (config.lastRefreshAt !== null) {
    const d = new Date(config.lastRefreshAt);
    fields.push({
      label: 'Token refreshed',
      value: Number.isNaN(d.getTime()) ? config.lastRefreshAt : d.toLocaleString(),
    });
  }
  fields.push({
    label: 'Channel id',
    value: <span className="font-mono text-xs">{config.id}</span>,
  });
  return fields;
}
import {
  useConnectorCatalogue,
  useConnectorConfigs,
  useConnectionCheck,
  useConnectorHealth,
  useCreateConnectorConfig,
  useCreateDestination,
  useCreateSource,
  useDestinations,
  usePublishTargets,
  useRunSync,
  useSetPublishTarget,
  useSources,
  useStartOAuth,
} from '../api/hooks.js';

/**
 * Connectors, Sources and Destinations (§14). A connector configuration holds
 * the credentials; a Source pulls assets in; a Destination pushes them out.
 */

/**
 * Connector key → brand mark (§3.3). A selection of the platforms INTEGRA
 * connects to; anything without a mark falls back to a mono initial tile, so the
 * grid still reads as a cohesive wall rather than a gap.
 */
const CONNECTOR_LOGO: Record<string, string> = {
  linkedin: 'linkedin',
  'linkedin-ads': 'linkedin',
  'meta-graph': 'meta',
  'meta-ads': 'meta',
  facebook: 'facebook',
  instagram: 'instagram',
  'tiktok-shop': 'tiktok',
  'tiktok-content': 'tiktok',
  youtube: 'youtube',
  pinterest: 'pinterest',
  wordpress: 'wordpress',
  threads: 'threads',
  bluesky: 'bluesky',
  x: 'x',
  twitter: 'x',
  whatsapp: 'whatsapp',
  snapchat: 'snapchat',
  'google-merchant': 'google',
  'google-ads': 'googleads',
  'google-analytics': 'googleanalytics',
  mailchimp: 'mailchimp',
  hubspot: 'hubspot',
};

/** A brand mark on a dark tile, or a mono initial when no mark is known. */
function ConnectorGlyph({ connectorKey }: { connectorKey: string }) {
  const logoId = CONNECTOR_LOGO[connectorKey];
  return (
    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-navy-800 text-ink-inverse">
      {logoId !== undefined ? (
        <BrandLogo id={logoId} size={20} className="text-ink-inverse" />
      ) : (
        <span className="font-mono text-sm font-semibold uppercase">
          {connectorKey.slice(0, 2)}
        </span>
      )}
    </span>
  );
}

/**
 * Turn a raw OAuth callback error code into plain, actionable guidance. A bare
 * code like "unauthorized_scope_error" tells an operator nothing; this names the
 * cause and the fix so a failed one-click connection is a next step, not a wall.
 */
function oauthErrorHelp(code: string): { title: string; message: string } {
  switch (code) {
    case 'unauthorized_scope_error':
      return {
        title: 'The platform hasn’t approved this app for the permissions it needs',
        message:
          'The provider refused the posting scopes the connection asked for. In that platform’s developer portal, add and get the product that grants them approved — for LinkedIn Company Pages that’s the Community Management API — then try again. You can also paste a token that already carries those scopes.',
      };
    case 'access_denied':
      return {
        title: 'Authorisation was declined',
        message:
          'Sign-in was cancelled or access was declined on the platform. Try again and approve the requested permissions.',
      };
    case 'redirect_uri_mismatch':
      return {
        title: 'The app’s redirect URL isn’t registered',
        message:
          'The provider app’s authorised redirect URL must exactly match this workspace’s callback. Add it in the developer portal, then try again.',
      };
    case 'invalid_client':
      return {
        title: 'The app’s client credentials were rejected',
        message:
          'The platform did not accept the configured client id/secret. Check them in the developer portal and in this deployment’s OAuth configuration.',
      };
    case 'missing_code':
      return {
        title: 'The platform didn’t return an authorisation code',
        message:
          'The connection came back without an authorisation code. Try again; if it persists, connect with a token instead.',
      };
    default:
      return {
        title: 'Connection didn’t complete',
        message: `The one-click connection was cancelled or failed (${code}). You can try again, or connect with a token.`,
      };
  }
}

type Parsed<T> = { ok: true; value: T } | { ok: false; error: string };

/**
 * Parse operator-typed JSON without ever throwing. A malformed paste is a
 * field-level validation failure, not a crashed screen.
 */
function parseJsonObject(text: string): Parsed<Record<string, unknown>> {
  const trimmed = text.trim();
  if (trimmed === '') return { ok: true, value: {} };
  let parsed: unknown;
  try {
    parsed = JSON.parse(trimmed);
  } catch {
    return { ok: false, error: 'Not valid JSON.' };
  }
  if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
    return { ok: false, error: 'Must be a JSON object, e.g. {"apiKey": "…"}.' };
  }
  return { ok: true, value: parsed as Record<string, unknown> };
}

function parseFieldMap(text: string): Parsed<Record<string, string>> {
  const object = parseJsonObject(text);
  if (!object.ok) return object;
  const out: Record<string, string> = {};
  for (const [key, value] of Object.entries(object.value)) {
    if (typeof value !== 'string') {
      return { ok: false, error: `Mapping for "${key}" must be a string field name.` };
    }
    out[key] = value;
  }
  return { ok: true, value: out };
}

/** Review expectation, mapped to a calm badge (§7 onboarding). */
const REVIEW_BADGE: Record<ConnectorReviewLevel, { tone: 'success' | 'warning'; label: string }> = {
  none: { tone: 'success', label: 'Self-serve' },
  quick: { tone: 'warning', label: 'Fast approval' },
  approval: { tone: 'warning', label: 'Review needed' },
};

/**
 * In-product setup guidance for a connector (§7 onboarding). Shown above the
 * credential inputs so an operator knows exactly where to create the app, which
 * scopes to request, what review to expect, and what each field is.
 */
function SetupGuidePanel({ guide }: { guide: ConnectorSetupGuide }) {
  const badge = REVIEW_BADGE[guide.review];
  return (
    <section
      aria-label="Setup guide"
      className="rounded-lg border border-edge-strong bg-surface-sunken p-3"
    >
      <Stack gap="sm">
        <Cluster gap="sm" align="center">
          <p className="text-xs font-semibold uppercase tracking-wide text-ink-muted">
            Setup guide
          </p>
          <Badge tone={badge.tone} dot>
            {badge.label}
          </Badge>
        </Cluster>

        <p className="text-sm text-ink">{guide.summary}</p>

        {guide.reviewNote !== undefined && (
          <p className="text-xs text-ink-muted">{guide.reviewNote}</p>
        )}

        <Cluster gap="sm">
          <a
            href={guide.developerPortalUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm font-semibold text-navy-700 hover:underline"
          >
            Open developer portal ↗
          </a>
          {guide.docsUrl !== undefined && (
            <a
              href={guide.docsUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm text-ink-muted hover:underline"
            >
              Docs ↗
            </a>
          )}
        </Cluster>

        {guide.steps.length > 0 && (
          <ol className="list-decimal space-y-1 pl-5 text-xs text-ink">
            {guide.steps.map((step, index) => (
              <li key={`${String(index)}-${step}`}>{step}</li>
            ))}
          </ol>
        )}

        {guide.scopes !== undefined && guide.scopes.length > 0 && (
          <div className="flex flex-col gap-1">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-ink-muted">
              Scopes / permissions
            </p>
            <div className="flex flex-wrap gap-1">
              {guide.scopes.map((scope) => (
                <span
                  key={scope}
                  className="rounded border border-edge-strong bg-surface px-1.5 py-0.5 font-mono text-[11px] text-ink-muted"
                >
                  {scope}
                </span>
              ))}
            </div>
          </div>
        )}

        {guide.fields.length > 0 && (
          <div className="flex flex-col gap-1">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-ink-muted">
              What each field is
            </p>
            <ul className="space-y-0.5 text-xs text-ink-muted">
              {guide.fields.map((field) => (
                <li key={field.key}>
                  <span className="font-semibold text-ink">{field.label}</span>
                  {field.required ? ' (required)' : ''}
                  {field.help !== undefined ? ` — ${field.help}` : ''}
                </li>
              ))}
            </ul>
          </div>
        )}
      </Stack>
    </section>
  );
}

/** One guided connector field — a labelled input, select, or write-only secret. */
function ConfigFieldInput({
  field,
  value,
  onChange,
  idPrefix,
}: {
  field: ConnectorField;
  value: string;
  onChange: (value: string) => void;
  idPrefix: string;
}) {
  const id = `${idPrefix}-${field.key}`;
  const label = field.required === true ? `${field.label} *` : field.label;
  return (
    <Field label={label} htmlFor={id} {...(field.help === undefined ? {} : { hint: field.help })}>
      {field.options !== undefined ? (
        <Select id={id} value={value} onChange={(e) => onChange(e.target.value)}>
          {field.options.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </Select>
      ) : (
        <Input
          id={id}
          type={field.secret === true ? 'password' : 'text'}
          autoComplete={field.secret === true ? 'new-password' : 'off'}
          value={value}
          placeholder={field.placeholder ?? ''}
          onChange={(e) => onChange(e.target.value)}
        />
      )}
    </Field>
  );
}

/**
 * Connectors whose account owns MORE THAN ONE destination, so after connecting
 * the user must choose which Page / board / account this channel publishes to
 * (§7.1 "Lane B"). The server is the source of truth — it lists the real options
 * and rejects a choose on a connector that doesn't support it — but the client
 * needs to know which rows to offer the picker on without a probe per row.
 */
const OWNER_SELECT_CONNECTORS = new Set([
  'meta-graph',
  'pinterest',
  'google-merchant',
  'linkedin',
  // A source, not a destination: it reads from the chosen GA4 property (§7.1).
  'google-analytics',
]);

/** Source connectors that pick a read-target rather than a publish destination. */
const SOURCE_OWNER_CONNECTORS = new Set(['google-analytics']);

/** A short human label for the destination a config currently points at, if any. */
function ownerSummary(config: ConnectorConfig): string | null {
  const s = config.settings;
  const str = (k: string): string | null =>
    typeof s[k] === 'string' && (s[k] as string).length > 0 ? (s[k] as string) : null;
  switch (config.connectorKey) {
    case 'pinterest': {
      const id = str('boardId');
      return id === null ? null : `Board ${id}`;
    }
    case 'google-merchant': {
      const id = str('merchantId');
      return id === null ? null : `Merchant ${id}`;
    }
    case 'meta-graph': {
      if (str('target') === 'instagram' && str('igUserId') !== null) {
        return `Instagram ${str('igUserId') ?? ''}`;
      }
      const page = str('pageId');
      return page === null ? null : `Page ${page}`;
    }
    case 'linkedin': {
      const author = str('authorId');
      if (author === null) return null;
      const orgPrefix = 'urn:li:organization:';
      return author.startsWith(orgPrefix) ? `Page ${author.slice(orgPrefix.length)}` : author;
    }
    case 'google-analytics': {
      const id = str('ga4PropertyId');
      return id === null ? null : `Property ${id}`;
    }
    default:
      return null;
  }
}

/** Is this target the one the config already points at? (every applied key matches) */
function isCurrentTarget(config: ConnectorConfig, target: PublishTarget): boolean {
  return Object.entries(target.applySettings).every(([k, v]) => config.settings[k] === v);
}

/**
 * The Lane-B picker: lists the real destinations the connected account can reach
 * and stores the chosen one. Fetches lazily (only while open) and reads the
 * account read-only — nothing is published (§7.4).
 */
function PublishTargetPicker({
  workspaceId,
  config,
  onClose,
}: {
  workspaceId: string;
  config: ConnectorConfig;
  onClose: () => void;
}) {
  const targets = usePublishTargets(workspaceId, config.id);
  const setTarget = useSetPublishTarget(workspaceId);
  const reads = SOURCE_OWNER_CONNECTORS.has(config.connectorKey);
  const noun =
    config.connectorKey === 'pinterest'
      ? 'board'
      : config.connectorKey === 'google-merchant'
        ? 'Merchant Center account'
        : config.connectorKey === 'linkedin'
          ? 'LinkedIn Page'
          : config.connectorKey === 'google-analytics'
            ? 'GA4 property'
            : 'Page or Instagram account';
  const list = targets.data?.data ?? [];

  return (
    <Modal
      open
      title={
        reads
          ? `Which ${noun} should ${config.displayName} read?`
          : `Where should ${config.displayName} publish?`
      }
      onClose={onClose}
      footer={
        <Button variant="secondary" onClick={onClose}>
          Close
        </Button>
      }
    >
      <Stack gap="md">
        <p className="text-xs text-ink-muted">
          {reads
            ? `Pick the ${noun} this source reads. This reads your connected account — nothing is changed.`
            : `Pick the ${noun} this channel posts to. This reads your connected account — nothing is published.`}
        </p>
        <QueryState
          loading={targets.isLoading}
          error={targets.error}
          what={reads ? 'your properties' : 'your destinations'}
        >
          {list.length === 0 ? (
            <EmptyState
              icon={TargetIcon}
              title="Nothing to choose yet"
              description={`This account has no ${noun} available. Create one on the platform, then reopen this.`}
              features={[
                { icon: EyeIcon, label: 'Reads your account — nothing is changed' },
                { icon: TargetIcon, label: 'Pick the exact property to read or post' },
                { icon: ShieldIcon, label: 'Scoped, read-only account access' },
              ]}
            />
          ) : (
            <Stack gap="sm">
              {list.map((target) => {
                const selected = isCurrentTarget(config, target);
                return (
                  <div
                    key={target.id}
                    className="flex items-center justify-between gap-3 rounded-md border border-edge px-3 py-2"
                  >
                    <div className="min-w-0">
                      <p className="truncate text-sm font-semibold text-navy-700">{target.label}</p>
                      {target.subtitle !== undefined && (
                        <p className="truncate text-xs text-ink-muted">{target.subtitle}</p>
                      )}
                    </div>
                    <Button
                      size="sm"
                      variant={selected ? 'secondary' : 'primary'}
                      disabled={selected}
                      loading={setTarget.isPending}
                      onClick={() =>
                        setTarget.mutate(
                          { connectorConfigId: config.id, applySettings: target.applySettings },
                          { onSuccess: onClose },
                        )
                      }
                    >
                      {selected ? 'Selected' : 'Use this'}
                    </Button>
                  </div>
                );
              })}
            </Stack>
          )}
        </QueryState>
        {setTarget.error instanceof Error && (
          <Alert tone="error" title="Could not save selection">
            {setTarget.error.message}
          </Alert>
        )}
      </Stack>
    </Modal>
  );
}

/** One segmented-control tab on the Integrations page. */
function TabButton({
  active,
  onClick,
  label,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cx(
        'rounded-md border px-3 py-1.5 text-sm font-medium transition-colors duration-fast',
        active
          ? 'border-navy-700 bg-navy-700 text-ink-inverse'
          : 'border-edge text-navy-700 hover:border-navy-400',
      )}
    >
      {label}
    </button>
  );
}

export function ConnectorsPage() {
  const { workspaceId = '' } = useParams<{ workspaceId: string }>();

  const catalogue = useConnectorCatalogue();
  const configs = useConnectorConfigs(workspaceId);
  const health = useConnectorHealth(workspaceId);
  const [inspectingChannel, setInspectingChannel] = useState<ConnectorConfig | null>(null);
  const [targetPickerConfig, setTargetPickerConfig] = useState<ConnectorConfig | null>(null);
  // One job per view: the page is an overview by default, with Sources and
  // Destinations on their own tabs and their add-forms behind a button — so no
  // single scroll stacks three tables and two forms.
  const [tab, setTab] = useState<'channels' | 'sources' | 'destinations'>('channels');
  const [showAddSource, setShowAddSource] = useState(false);
  const [showAddDestination, setShowAddDestination] = useState(false);
  const connectionCheck = useConnectionCheck(workspaceId);
  const sources = useSources(workspaceId);
  const destinations = useDestinations(workspaceId);

  const createConfig = useCreateConnectorConfig(workspaceId);
  const createSource = useCreateSource(workspaceId);
  const createDestination = useCreateDestination(workspaceId);
  const runSync = useRunSync(workspaceId);
  const startOAuth = useStartOAuth(workspaceId);

  // The OAuth callback returns here with ?connected=<key> or ?oauth_error=<code>,
  // so a one-click connection lands the user back on this page with a verdict.
  const [searchParams, setSearchParams] = useSearchParams();
  const connectedKey = searchParams.get('connected');
  const oauthError = searchParams.get('oauth_error');
  const clearOAuthBanner = (): void => {
    const next = new URLSearchParams(searchParams);
    next.delete('connected');
    next.delete('oauth_error');
    setSearchParams(next, { replace: true });
  };

  // --- Add connector form state ---
  // Credentials and settings are per-field maps, driven by the connector's
  // catalogue schema — no hand-authored JSON (§17.2 guided forms).
  const [configOpen, setConfigOpen] = useState(false);
  const [connectorKey, setConnectorKey] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [credentialValues, setCredentialValues] = useState<Record<string, string>>({});
  const [settingValues, setSettingValues] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  // Catalogue browse state — search + type filter for the connector picker.
  const [catalogueSearch, setCatalogueSearch] = useState('');
  const [catalogueType, setCatalogueType] = useState<'all' | 'sources' | 'destinations'>('all');

  // --- Add source form state ---
  const [sourceConfigId, setSourceConfigId] = useState('');
  const [fieldMapText, setFieldMapText] = useState('');
  const [requiredFieldsText, setRequiredFieldsText] = useState('');
  const [syncMode, setSyncMode] = useState('full');
  const [cadenceCron, setCadenceCron] = useState('');
  const [conflictResolution, setConflictResolution] = useState('source_wins');
  const [fieldMapError, setFieldMapError] = useState<string | null>(null);

  // --- Add destination form state ---
  const [destinationConfigId, setDestinationConfigId] = useState('');

  // Which source last produced the summary now on screen.
  const [syncedSourceId, setSyncedSourceId] = useState<string | null>(null);
  // Which connector the report on screen belongs to, so a stale report from a
  // previous row is never shown against a different connector.
  const [checkedConfigId, setCheckedConfigId] = useState<string | null>(null);

  const configList = configs.data?.data ?? [];
  const configName = (id: string): string =>
    configList.find((config) => config.id === id)?.displayName ?? id;

  const form = connectorForm(connectorKey);
  const selectedEntry = (catalogue.data?.data ?? []).find(
    (entry) => entry.connectorKey === connectorKey,
  );
  const setupGuide = selectedEntry?.setupGuide;
  const oauthReady = selectedEntry?.oauth?.supported === true;

  // Catalogue derivations — which connectors are already configured, and the
  // search/type-filtered view for the picker grid.
  const configuredKeys = new Set(configList.map((c) => c.connectorKey));
  const catalogueEntries = catalogue.data?.data ?? [];
  const filteredCatalogue = catalogueEntries.filter((entry) => {
    const dir = entry.direction.toLowerCase();
    const typeOk =
      catalogueType === 'all' ||
      (catalogueType === 'sources' && (dir === 'source' || dir === 'bidirectional')) ||
      (catalogueType === 'destinations' && (dir === 'destination' || dir === 'bidirectional'));
    const q = catalogueSearch.trim().toLowerCase();
    const searchOk =
      q === '' || entry.name.toLowerCase().includes(q) || entry.connectorKey.includes(q);
    return typeOk && searchOk;
  });

  /** One-click connect straight from a catalogue card — no select-then-scroll. */
  const connectOAuthFor = (entry: { connectorKey: string; name: string }): void => {
    startOAuth.mutate(
      { connectorKey: entry.connectorKey, displayName: entry.name },
      { onSuccess: (r) => window.location.assign(r.authorizeUrl) },
    );
  };

  /** Launch the one-click flow: fetch the authorize URL, then hand off the browser. */
  const connectWithOAuth = (): void => {
    startOAuth.mutate(
      { connectorKey, displayName: displayName.trim() === '' ? connectorKey : displayName.trim() },
      {
        onSuccess: (r) => {
          window.location.assign(r.authorizeUrl);
        },
      },
    );
  };

  /** Reset the field values when the operator picks a different connector. */
  const chooseConnector = (key: string): void => {
    setConnectorKey(key);
    const next = connectorForm(key);
    const defaults = (fields: readonly ConnectorField[]): Record<string, string> =>
      Object.fromEntries(
        fields
          .filter((f) => f.defaultValue !== undefined)
          .map((f) => [f.key, f.defaultValue ?? '']),
      );
    setCredentialValues(defaults(next.credentials));
    setSettingValues(defaults(next.settings));
    setFormError(null);
  };

  const submitConfig = (): void => {
    if (connectorKey === '') {
      setFormError('Choose a connector first.');
      return;
    }
    // Only non-empty values are sent; a missing required field is a validation
    // failure, not an empty string persisted to the channel.
    const collect = (fields: readonly ConnectorField[], values: Record<string, string>) => {
      const out: Record<string, string> = {};
      const missing: string[] = [];
      for (const field of fields) {
        const value = (values[field.key] ?? '').trim();
        if (value === '') {
          if (field.required === true) missing.push(field.label);
          continue;
        }
        out[field.key] = value;
      }
      return { out, missing };
    };
    const creds = collect(form.credentials, credentialValues);
    const setts = collect(form.settings, settingValues);
    const missing = [...creds.missing, ...setts.missing];
    if (missing.length > 0) {
      setFormError(
        `Fill the required field${missing.length > 1 ? 's' : ''}: ${missing.join(', ')}.`,
      );
      return;
    }
    setFormError(null);

    createConfig.mutate(
      {
        connectorKey,
        displayName: displayName.trim() === '' ? connectorKey : displayName.trim(),
        credentials: creds.out,
        settings: setts.out,
      },
      {
        onSuccess: () => {
          setConfigOpen(false);
          // Never keep a credential in component state a moment longer than the
          // request needs it (§14.3).
          setCredentialValues({});
          setSettingValues({});
          setConnectorKey('');
          setDisplayName('');
        },
      },
    );
  };

  const submitSource = (): void => {
    const fieldMap = parseFieldMap(fieldMapText);
    setFieldMapError(fieldMap.ok ? null : fieldMap.error);
    if (!fieldMap.ok || sourceConfigId === '') return;

    createSource.mutate(
      {
        connectorConfigId: sourceConfigId,
        schemaMapping: {
          fieldMap: fieldMap.value,
          requiredFields: requiredFieldsText
            .split(',')
            .map((field) => field.trim())
            .filter((field) => field !== ''),
        },
        syncConfig: {
          mode: syncMode,
          // An empty cadence means manual-only, expressed as null not ''.
          cadenceCron: cadenceCron.trim() === '' ? null : cadenceCron.trim(),
          conflictResolution,
        },
      },
      {
        onSuccess: () => {
          setFieldMapText('');
          setRequiredFieldsText('');
          setCadenceCron('');
        },
      },
    );
  };

  const sourceColumns: readonly Column<Source>[] = [
    {
      key: 'connector',
      header: 'Connector',
      sortValue: (row) => configName(row.connectorConfigId).toLowerCase(),
      render: (row) => (
        <span className="text-sm font-semibold text-navy-700">
          {configName(row.connectorConfigId)}
        </span>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      sortValue: (row) => row.status,
      render: (row) => <Badge dot>{row.status}</Badge>,
    },
    {
      key: 'lastSyncAt',
      header: 'Last sync',
      sortValue: (row) => (row.lastSyncAt === null ? 0 : new Date(row.lastSyncAt).getTime()),
      render: (row) =>
        row.lastSyncAt === null ? (
          <span className="text-xs text-ink-muted">Never synced</span>
        ) : (
          <span className="font-mono text-xs text-ink">{row.lastSyncAt}</span>
        ),
    },
    {
      key: 'actions',
      header: '',
      align: 'right',
      render: (row) => (
        <Button
          size="sm"
          variant="secondary"
          loading={runSync.isPending && syncedSourceId === row.id}
          onClick={() => {
            setSyncedSourceId(row.id);
            runSync.mutate(row.id);
          }}
        >
          Sync now
        </Button>
      ),
    },
  ];

  const destinationColumns: readonly Column<Destination>[] = [
    {
      key: 'connector',
      header: 'Connector',
      sortValue: (row) => configName(row.connectorConfigId).toLowerCase(),
      render: (row) => (
        <span className="text-sm font-semibold text-navy-700">
          {configName(row.connectorConfigId)}
        </span>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      sortValue: (row) => row.status,
      render: (row) => <Badge dot>{row.status}</Badge>,
    },
    {
      key: 'fileTypes',
      header: 'Accepted file types',
      render: (row) => (
        <span className="text-xs text-ink-muted">
          {row.formatSpec.acceptedFileTypes.length === 0
            ? 'unspecified'
            : row.formatSpec.acceptedFileTypes.join(', ')}
        </span>
      ),
    },
  ];

  const summary: SyncSummary | undefined = runSync.data;
  const report = connectionCheck.data?.data;

  return (
    <Page
      title="Integrations"
      icon={ConnectorIcon}
      description="Connect every tool this workspace runs on — analytics, CRM, email, ads and design — alongside the channels you publish to. One hub, one-click where supported."
      stats={
        configList.length > 0 ||
        (sources.data?.data.length ?? 0) > 0 ||
        (destinations.data?.data.length ?? 0) > 0 ? (
          <StatStrip
            stats={[
              { icon: ConnectorIcon, label: 'Connectors', value: configList.length },
              { icon: LinkIcon, label: 'Sources', value: sources.data?.data.length ?? 0 },
              {
                icon: GlobeIcon,
                label: 'Destinations',
                value: destinations.data?.data.length ?? 0,
                tone: 'accent',
              },
            ]}
          />
        ) : undefined
      }
      actions={
        tab === 'channels' ? (
          <Button onClick={() => setConfigOpen(true)}>Add connector</Button>
        ) : tab === 'sources' ? (
          <Button onClick={() => setShowAddSource(true)}>Add source</Button>
        ) : (
          <Button onClick={() => setShowAddDestination(true)}>Add destination</Button>
        )
      }
    >
      <Stack gap="lg">
        {/* Verdict from a one-click connection round-trip (OAuth callback). */}
        {connectedKey !== null && (
          <Alert tone="success" title="Channel connected">
            <Cluster justify="between" align="center">
              <span>
                {connectedKey} is connected via one-click authorisation. Open the Destinations tab
                to point a destination at it and start publishing.
              </span>
              <Button size="sm" variant="ghost" onClick={clearOAuthBanner}>
                Dismiss
              </Button>
            </Cluster>
          </Alert>
        )}
        {oauthError !== null && (
          <Alert tone="error" title={oauthErrorHelp(oauthError).title}>
            <Cluster justify="between" align="start" gap="md">
              <span>{oauthErrorHelp(oauthError).message}</span>
              <Button size="sm" variant="ghost" onClick={clearOAuthBanner}>
                Dismiss
              </Button>
            </Cluster>
          </Alert>
        )}

        <Cluster gap="sm">
          <TabButton
            active={tab === 'channels'}
            onClick={() => setTab('channels')}
            label={`Channels (${String(configList.length)})`}
          />
          <TabButton
            active={tab === 'sources'}
            onClick={() => setTab('sources')}
            label={`Sources (${String(sources.data?.data.length ?? 0)})`}
          />
          <TabButton
            active={tab === 'destinations'}
            onClick={() => setTab('destinations')}
            label={`Destinations (${String(destinations.data?.data.length ?? 0)})`}
          />
        </Cluster>

        {tab === 'channels' && (
          <Card
            title="Configured connectors"
            subtitle="Credentials are write-only: once saved they can never be read back. Only whether they exist is shown."
          >
            <QueryState loading={configs.isLoading} error={configs.error} what="connectors">
              {configList.length === 0 ? (
                <EmptyState
                  icon={ConnectorIcon}
                  title="No connectors configured"
                  description="Add one to give this workspace a channel to pull from or publish to."
                  action={<Button onClick={() => setConfigOpen(true)}>Add connector</Button>}
                  features={[
                    { icon: LinkIcon, label: 'One-click connect where supported' },
                    { icon: LockIcon, label: 'Credentials are write-only, never read back' },
                    { icon: GlobeIcon, label: 'Analytics, CRM, email, ads & channels' },
                  ]}
                />
              ) : (
                <Grid min="sm" gap="md">
                  {configList.map((row) => {
                    const entry = health.data?.data.find((h) => h.connectorConfigId === row.id);
                    const owner = OWNER_SELECT_CONNECTORS.has(row.connectorKey)
                      ? ownerSummary(row)
                      : undefined;
                    return (
                      <div
                        key={row.id}
                        className="flex flex-col gap-3 rounded-lg border border-edge bg-surface p-4 shadow-card"
                      >
                        <div className="flex items-start gap-3">
                          <ConnectorGlyph connectorKey={row.connectorKey} />
                          <div className="min-w-0 flex-1">
                            <p className="truncate text-sm font-semibold text-navy-700">
                              {row.displayName}
                            </p>
                            <p className="truncate font-mono text-[11px] text-ink-muted">
                              {row.connectorKey}
                            </p>
                          </div>
                          <Badge tone={row.hasCredentials ? 'success' : 'warning'} dot>
                            {row.hasCredentials ? 'Stored' : 'Missing'}
                          </Badge>
                        </div>

                        <div className="flex flex-wrap items-center justify-between gap-2">
                          {entry === undefined ? (
                            <span className="text-xs text-ink-muted">Not yet checked</span>
                          ) : (
                            <ConnectorStatusBadge
                              healthy={entry.healthy}
                              circuitState={entry.circuitState}
                              detail={entry.detail}
                            />
                          )}
                          {owner !== undefined && (
                            <Cluster gap="sm" align="center">
                              {owner === null ? (
                                <Badge tone="warning" dot>
                                  No owner
                                </Badge>
                              ) : (
                                <span className="max-w-[10rem] truncate text-xs text-ink">
                                  {owner}
                                </span>
                              )}
                              <button
                                type="button"
                                onClick={() => setTargetPickerConfig(row)}
                                className="text-xs font-semibold text-navy-700 hover:underline"
                              >
                                {owner === null ? 'Choose' : 'Change'}
                              </button>
                            </Cluster>
                          )}
                        </div>

                        <div className="mt-auto flex items-center justify-end gap-3 border-t border-edge pt-3">
                          <button
                            type="button"
                            onClick={() => setInspectingChannel(row)}
                            className="text-xs text-ink-muted hover:text-navy-700 hover:underline"
                          >
                            Inspect
                          </button>
                          <Button
                            size="sm"
                            variant="secondary"
                            loading={connectionCheck.isPending && checkedConfigId === row.id}
                            onClick={() => {
                              setCheckedConfigId(row.id);
                              connectionCheck.mutate(row.id);
                            }}
                          >
                            Test connection
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </Grid>
              )}
            </QueryState>

            {connectionCheck.error instanceof Error && (
              <Alert tone="error" title="Connection check could not run">
                {connectionCheck.error.message}
              </Alert>
            )}

            {report !== undefined && (
              <Alert
                tone={report.reachable ? 'success' : 'error'}
                title={
                  report.reachable
                    ? `${report.connectorName} is reachable`
                    : `${report.connectorName} is not reachable`
                }
              >
                <Stack gap="sm">
                  <p className="text-xs">
                    Read-only check: health, quota and one page of resources. Nothing was created,
                    changed or removed in the channel.
                  </p>
                  <ul className="text-xs">
                    {report.probes.map((probe) => (
                      <li key={probe.probe} className="font-mono">
                        {probe.ok ? 'PASS' : 'FAIL'} {probe.probe}: {probe.detail} (
                        {probe.durationMs}
                        ms)
                      </li>
                    ))}
                  </ul>
                </Stack>
              </Alert>
            )}
          </Card>
        )}

        {tab === 'sources' && (
          <Card title="Sources" subtitle="Where assets come in from.">
            <Stack gap="lg">
              <QueryState loading={sources.isLoading} error={sources.error} what="sources">
                <Table
                  columns={sourceColumns}
                  rows={sources.data?.data ?? []}
                  rowKey={(row) => row.id}
                  empty={
                    <EmptyState
                      icon={LinkIcon}
                      title="No sources yet"
                      description="A source pulls a catalogue in — products, articles, images — so you have something to publish. Add a source to connect one."
                      features={[
                        { icon: LayersIcon, label: 'Pull products, articles & images in' },
                        { icon: ConnectorIcon, label: 'Connect any catalogue you already run' },
                        { icon: CheckCircleIcon, label: 'Every sync is versioned and audited' },
                      ]}
                    />
                  }
                />
              </QueryState>

              {runSync.error instanceof Error && (
                <Alert tone="error" title="Sync failed">
                  {runSync.error.message}
                </Alert>
              )}

              {summary !== undefined && (
                // A sync that skipped rows or logged errors is NOT a clean
                // success, and must not be dressed as one (§2.7).
                <Alert
                  tone={
                    summary.errors.length > 0
                      ? 'error'
                      : summary.skipped > 0
                        ? 'warning'
                        : 'success'
                  }
                  title={
                    summary.errors.length > 0
                      ? 'Sync finished with errors'
                      : summary.skipped > 0
                        ? 'Sync finished, some rows skipped'
                        : 'Sync finished cleanly'
                  }
                >
                  <Stack gap="sm">
                    <p>
                      {summary.created} created · {summary.updated} updated · {summary.skipped}{' '}
                      skipped
                    </p>
                    {summary.errors.length > 0 && (
                      <ul className="list-disc pl-5 text-xs">
                        {summary.errors.map((message, index) => (
                          <li key={`${String(index)}-${message}`}>{message}</li>
                        ))}
                      </ul>
                    )}
                    <p className="font-mono text-xs">operation {summary.syncOperationId}</p>
                  </Stack>
                </Alert>
              )}

              {showAddSource && (
                <Stack gap="md">
                  <p className="text-sm font-semibold text-navy-700">Add a source</p>
                  <Field label="Connector" htmlFor="source-config">
                    <Select
                      id="source-config"
                      value={sourceConfigId}
                      onChange={(e) => setSourceConfigId(e.target.value)}
                    >
                      <option value="">Choose a configured connector…</option>
                      {configList.map((config) => (
                        <option key={config.id} value={config.id}>
                          {config.displayName}
                        </option>
                      ))}
                    </Select>
                  </Field>
                  <Field
                    label="Field map"
                    htmlFor="source-fieldmap"
                    hint={'JSON mapping INTEGRA fields to the channel’s, e.g. {"title": "name"}.'}
                    {...(fieldMapError === null ? {} : { error: fieldMapError })}
                  >
                    <TextArea
                      id="source-fieldmap"
                      value={fieldMapText}
                      onChange={(e) => setFieldMapText(e.target.value)}
                      placeholder={'{\n  "title": "name"\n}'}
                    />
                  </Field>
                  <Field
                    label="Required fields"
                    htmlFor="source-required"
                    hint="Comma separated. A row missing one of these is skipped rather than half-ingested."
                  >
                    <Input
                      id="source-required"
                      value={requiredFieldsText}
                      onChange={(e) => setRequiredFieldsText(e.target.value)}
                      placeholder="title, price"
                    />
                  </Field>
                  <Cluster gap="md" align="end">
                    <Field label="Mode" htmlFor="source-mode">
                      <Select
                        id="source-mode"
                        value={syncMode}
                        onChange={(e) => setSyncMode(e.target.value)}
                      >
                        <option value="full">full</option>
                        <option value="incremental">incremental</option>
                      </Select>
                    </Field>
                    <Field
                      label="Cadence (cron)"
                      htmlFor="source-cron"
                      hint="Leave blank for manual syncs only."
                    >
                      <Input
                        id="source-cron"
                        value={cadenceCron}
                        onChange={(e) => setCadenceCron(e.target.value)}
                        placeholder="0 * * * *"
                      />
                    </Field>
                    <Field label="Conflicts" htmlFor="source-conflict">
                      <Select
                        id="source-conflict"
                        value={conflictResolution}
                        onChange={(e) => setConflictResolution(e.target.value)}
                      >
                        <option value="source_wins">source wins</option>
                        <option value="local_wins">local wins</option>
                        <option value="manual">manual</option>
                      </Select>
                    </Field>
                  </Cluster>
                  {createSource.error instanceof Error && (
                    <Alert tone="error" title="Could not create source">
                      {createSource.error.message}
                    </Alert>
                  )}
                  <Cluster>
                    <Button
                      onClick={submitSource}
                      loading={createSource.isPending}
                      disabled={sourceConfigId === ''}
                    >
                      Add source
                    </Button>
                    <Button variant="secondary" onClick={() => setShowAddSource(false)}>
                      Cancel
                    </Button>
                  </Cluster>
                </Stack>
              )}
            </Stack>
          </Card>
        )}

        {tab === 'destinations' && (
          <Card title="Destinations" subtitle="Where assets are published to.">
            <Stack gap="lg">
              <QueryState
                loading={destinations.isLoading}
                error={destinations.error}
                what="destinations"
              >
                <Table
                  columns={destinationColumns}
                  rows={destinations.data?.data ?? []}
                  rowKey={(row) => row.id}
                  empty={
                    <EmptyState
                      icon={GlobeIcon}
                      title="No destinations yet"
                      description="A destination is where content lands — a channel you publish to. Add a destination to point one at a connected channel."
                      features={[
                        { icon: GlobeIcon, label: 'Point content at a connected channel' },
                        { icon: RocketIcon, label: 'Publish once, land on every channel' },
                        { icon: ShieldIcon, label: 'Every publish passes an approval gate' },
                      ]}
                    />
                  }
                />
              </QueryState>

              {showAddDestination && (
                <Stack gap="md">
                  <p className="text-sm font-semibold text-navy-700">Add a destination</p>
                  <Field label="Connector" htmlFor="destination-config">
                    <Select
                      id="destination-config"
                      value={destinationConfigId}
                      onChange={(e) => setDestinationConfigId(e.target.value)}
                    >
                      <option value="">Choose a configured connector…</option>
                      {configList.map((config) => (
                        <option key={config.id} value={config.id}>
                          {config.displayName}
                        </option>
                      ))}
                    </Select>
                  </Field>
                  {createDestination.error instanceof Error && (
                    <Alert tone="error" title="Could not create destination">
                      {createDestination.error.message}
                    </Alert>
                  )}
                  <Cluster>
                    <Button
                      onClick={() =>
                        createDestination.mutate(
                          { connectorConfigId: destinationConfigId },
                          { onSuccess: () => setShowAddDestination(false) },
                        )
                      }
                      loading={createDestination.isPending}
                      disabled={destinationConfigId === ''}
                    >
                      Add destination
                    </Button>
                    <Button variant="secondary" onClick={() => setShowAddDestination(false)}>
                      Cancel
                    </Button>
                  </Cluster>
                </Stack>
              )}
            </Stack>
          </Card>
        )}
      </Stack>

      <Modal
        open={configOpen}
        title="Add connector"
        onClose={() => setConfigOpen(false)}
        footer={
          <>
            <Button variant="secondary" onClick={() => setConfigOpen(false)}>
              {connectorKey === '' ? 'Close' : 'Cancel'}
            </Button>
            {connectorKey !== '' && (
              <Button onClick={submitConfig} loading={createConfig.isPending}>
                Save
              </Button>
            )}
          </>
        }
      >
        <Stack gap="md">
          <QueryState loading={catalogue.isLoading} error={catalogue.error} what="the catalogue">
            <div>
              {/* Catalogue controls — search + type filter, Replit-style. */}
              <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                <div className="w-full sm:max-w-xs">
                  <Input
                    id="catalogue-search"
                    value={catalogueSearch}
                    onChange={(e) => setCatalogueSearch(e.target.value)}
                    placeholder="Search integrations…"
                  />
                </div>
                <Cluster gap="sm">
                  {(['all', 'sources', 'destinations'] as const).map((t) => (
                    <TabButton
                      key={t}
                      active={catalogueType === t}
                      onClick={() => setCatalogueType(t)}
                      label={t === 'all' ? 'All' : t === 'sources' ? 'Sources' : 'Destinations'}
                    />
                  ))}
                </Cluster>
              </div>
              <p className="mb-2 text-[11px] text-ink-muted">
                Showing {filteredCatalogue.length} of {catalogueEntries.length} integrations
              </p>

              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                {filteredCatalogue.map((entry) => {
                  const connected = configuredKeys.has(entry.connectorKey);
                  const oneClick = entry.oauth?.supported === true;
                  const selected = entry.connectorKey === connectorKey;
                  return (
                    <div
                      key={entry.connectorKey}
                      id={`catalogue-${entry.connectorKey}`}
                      role="button"
                      tabIndex={0}
                      onClick={() => chooseConnector(entry.connectorKey)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          chooseConnector(entry.connectorKey);
                        }
                      }}
                      className={cx(
                        'flex cursor-pointer flex-col gap-2.5 rounded-lg border p-3 text-left transition-colors duration-fast',
                        selected
                          ? 'border-gold-500 bg-gold-200/30'
                          : 'border-edge bg-surface hover:border-navy-400',
                      )}
                    >
                      <div className="flex items-start gap-2.5">
                        <ConnectorGlyph connectorKey={entry.connectorKey} />
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-sm font-semibold text-navy-700">
                            {entry.name}
                          </p>
                          <p className="text-[10px] uppercase tracking-wide text-ink-muted">
                            {entry.direction}
                          </p>
                        </div>
                        {connected ? (
                          <Badge tone="success" dot>
                            Connected
                          </Badge>
                        ) : oneClick ? (
                          <Badge tone="gold">1-click</Badge>
                        ) : null}
                      </div>
                      {oneClick && !connected && (
                        <Button
                          size="sm"
                          className="w-full"
                          loading={
                            startOAuth.isPending &&
                            startOAuth.variables?.connectorKey === entry.connectorKey
                          }
                          onClick={() => connectOAuthFor(entry)}
                        >
                          Connect {entry.name} →
                        </Button>
                      )}
                    </div>
                  );
                })}
                {filteredCatalogue.length === 0 && (
                  <p className="col-span-full py-6 text-center text-sm text-ink-muted">
                    No integrations match your search.
                  </p>
                )}
              </div>
            </div>
          </QueryState>

          {connectorKey !== '' && (
            <>
              {setupGuide !== undefined && <SetupGuidePanel guide={setupGuide} />}

              <Field label="Display name" htmlFor="config-name">
                <Input
                  id="config-name"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  placeholder={connectorKey}
                />
              </Field>

              {/* One-click: authorise INTEGRA on the platform — no token to mint or
                  paste. Shown only when INTEGRA holds approved OAuth credentials for
                  this provider; otherwise the manual token path below is the way. */}
              {oauthReady && (
                <div className="rounded-md border border-gold-400 bg-gold-200/40 p-3">
                  <p className="text-sm font-semibold text-navy-700">One-click connection</p>
                  <p className="mt-0.5 text-xs text-ink-muted">
                    Authorise INTEGRA on {selectedEntry?.name ?? connectorKey} — no token to
                    generate or paste. You&rsquo;ll return here once it&rsquo;s connected.
                  </p>
                  <Button
                    onClick={connectWithOAuth}
                    loading={startOAuth.isPending}
                    className="mt-2.5 w-full"
                  >
                    Connect with {selectedEntry?.name ?? connectorKey} →
                  </Button>
                  {startOAuth.error instanceof Error && (
                    <p className="mt-1.5 text-xs text-state-error">{startOAuth.error.message}</p>
                  )}
                </div>
              )}
              {oauthReady && form.credentials.length > 0 && (
                <div className="flex items-center gap-2 text-[11px] uppercase tracking-wide text-ink-muted">
                  <span className="h-px flex-1 bg-edge" aria-hidden="true" />
                  or connect with a token
                  <span className="h-px flex-1 bg-edge" aria-hidden="true" />
                </div>
              )}

              {form.note !== undefined && <Alert tone="info">{form.note}</Alert>}

              {form.credentials.length > 0 && (
                <Stack gap="sm">
                  <p className="text-xs font-semibold uppercase tracking-wide text-ink-muted">
                    Credentials
                  </p>
                  <p className="-mt-1 text-[11px] leading-snug text-ink-muted">
                    Write-only, stored encrypted and never shown again.
                    {form.credentialsHint !== undefined ? ` ${form.credentialsHint}` : ''}
                  </p>
                  {form.credentials.map((field) => (
                    <ConfigFieldInput
                      key={field.key}
                      field={field}
                      idPrefix="cred"
                      value={credentialValues[field.key] ?? ''}
                      onChange={(value) =>
                        setCredentialValues((current) => ({ ...current, [field.key]: value }))
                      }
                    />
                  ))}
                </Stack>
              )}

              {form.settings.length > 0 && (
                <Stack gap="sm">
                  <p className="text-xs font-semibold uppercase tracking-wide text-ink-muted">
                    Settings
                  </p>
                  {form.settings.map((field) => (
                    <ConfigFieldInput
                      key={field.key}
                      field={field}
                      idPrefix="set"
                      value={settingValues[field.key] ?? ''}
                      onChange={(value) =>
                        setSettingValues((current) => ({ ...current, [field.key]: value }))
                      }
                    />
                  ))}
                </Stack>
              )}

              {form.formats !== undefined && form.formats.length > 0 && (
                <Stack gap="sm">
                  <p className="text-xs font-semibold uppercase tracking-wide text-ink-muted">
                    Accepted creative
                  </p>
                  <div className="flex flex-wrap gap-3">
                    {form.formats.map((fmt) => (
                      <ChannelFormatPreview
                        key={`${String(fmt.width)}x${String(fmt.height)}`}
                        channel={`${String(fmt.width)}×${String(fmt.height)}`}
                        width={fmt.width}
                        height={fmt.height}
                        {...(form.maxFileSizeBytes === undefined
                          ? {}
                          : { maxFileSizeBytes: form.maxFileSizeBytes })}
                        {...(form.acceptedFileTypes === undefined
                          ? {}
                          : { accepted: form.acceptedFileTypes })}
                      />
                    ))}
                  </div>
                </Stack>
              )}
            </>
          )}

          {formError !== null && (
            <Alert tone="error" title="Check the form">
              {formError}
            </Alert>
          )}

          {createConfig.error instanceof Error && (
            <Alert tone="error" title="Could not save connector">
              {createConfig.error.message}
            </Alert>
          )}
        </Stack>
      </Modal>

      {targetPickerConfig !== null && (
        <PublishTargetPicker
          workspaceId={workspaceId}
          config={targetPickerConfig}
          onClose={() => setTargetPickerConfig(null)}
        />
      )}

      <Inspector
        open={inspectingChannel !== null}
        title={inspectingChannel?.displayName ?? 'Channel'}
        {...(inspectingChannel !== null ? { subtitle: 'Channel · provenance & live state' } : {})}
        onClose={() => setInspectingChannel(null)}
        fields={
          inspectingChannel !== null
            ? channelInspectorFields(
                inspectingChannel,
                health.data?.data.find((h) => h.connectorConfigId === inspectingChannel.id),
              )
            : []
        }
      />
    </Page>
  );
}
